import React from 'react';
import { Button, Popup } from 'semantic-ui-react';
import moment from 'moment-timezone';
import AGTAbleWrapper, { SortableHeader } from 'components/AGTable/AGTableWrapper';
import { PaginatedDiscountCodes, PromoCodeSortColumn, SortDirection, ValidatedDiscountCode } from 'types';
import Pagination from 'components/Pagination/Pagination';
import { PromoCodeDiscountType } from 'constants/promoCodes';

type PromoCodeTableProps = {
    promoCodes: PaginatedDiscountCodes;
    spotTimezone: string;
    deletePromoCode: (dc: ValidatedDiscountCode) => void;
    openQrCodeModal: (dc: ValidatedDiscountCode) => void;
    handlePaginationChange: (page: string) => void;
    sortColumn: PromoCodeSortColumn | null;
    sortDirection: SortDirection;
    onSortChange: (column: PromoCodeSortColumn) => void;
};

function PromoCodeTable({
    promoCodes,
    spotTimezone,
    deletePromoCode,
    openQrCodeModal,
    handlePaginationChange,
    sortColumn,
    sortDirection,
    onSortChange,
}: PromoCodeTableProps) {
    if (!promoCodes || promoCodes.results.length === 0) return <p>No discount codes</p>;

    return (
        <AGTAbleWrapper>
            <thead>
                <tr>
                    <SortableHeader
                        label="Code"
                        column="code"
                        activeColumn={sortColumn}
                        direction={sortDirection}
                        onSortChange={onSortChange}
                    />
                    <SortableHeader
                        label="Description"
                        column="notes"
                        activeColumn={sortColumn}
                        direction={sortDirection}
                        onSortChange={onSortChange}
                    />
                    <th>Valid For</th>
                    <th>Amount</th>
                    <th>Valid Date</th>
                    <th>Expiration</th>
                    <SortableHeader
                        label="Utilization"
                        column="uses_left"
                        activeColumn={sortColumn}
                        direction={sortDirection}
                        onSortChange={onSortChange}
                    />
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {promoCodes.results.map((promoCode) => {
                    // Converts UTC datetime from promocode to the timezone of the Spot

                    const hasExpDateAndTZ = promoCode?.expiration && !!spotTimezone;
                    const expirationDate = hasExpDateAndTZ
                        ? moment.utc(promoCode.expiration).tz(spotTimezone).format('L LT')
                        : 'Never';

                    const hasValidFromAndTZ = promoCode?.valid_from && !!spotTimezone;
                    const validFromDate = hasValidFromAndTZ
                        ? moment.utc(promoCode.valid_from).tz(spotTimezone).format('L LT')
                        : '';

                    const usesLeft = promoCode.number_of_uses
                        ? promoCode.number_of_uses - promoCode.total_uses
                        : promoCode.total_uses;
                    const usage = `${usesLeft} ${usesLeft === 1 ? 'use' : 'uses'}`;
                    const formattedValueSuffix = `${
                        promoCode.discount_type === PromoCodeDiscountType.MINUTES_TO_DISCOUNT ? 'free' : 'off'
                    }`;
                    const formattedApplicableRentalTypes = promoCode.applicable_rental_types
                        ? promoCode.applicable_rental_types
                              // Capitalize first letter of each word
                              .map((type) => type.replace(/\b\w/g, (l) => l.toUpperCase()))
                              .join(', ')
                        : 'All Rental Types';

                    return (
                        <tr key={promoCode.pk}>
                            <td
                                style={{
                                    maxWidth: 100,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Popup
                                    trigger={<span>{promoCode.code.toUpperCase()}</span>}
                                    content={promoCode.code.toUpperCase()}
                                    on="hover"
                                    inverted
                                    wide
                                >
                                    {promoCode.code.toUpperCase()}
                                </Popup>
                            </td>
                            <td>{promoCode.notes}</td>
                            <td>{formattedApplicableRentalTypes}</td>
                            <td>
                                {promoCode.discount_type === PromoCodeDiscountType.RATE_OVERRIDE
                                    ? promoCode.formatted_value
                                    : `${promoCode.formatted_value} ${formattedValueSuffix}`}
                            </td>
                            <td>{validFromDate}</td>
                            <td>{expirationDate}</td>
                            <td>
                                {promoCode.number_of_uses
                                    ? `${usage} left out of ${promoCode.number_of_uses}`
                                    : `${usage}, no limit`}
                            </td>
                            <td>
                                <Button
                                    size="mini"
                                    onClick={() => openQrCodeModal(promoCode)}
                                    icon="download"
                                    float="right"
                                    style={{ color: '#7A7A7A', backgroundColor: 'white' }}
                                />
                                <Button
                                    size="mini"
                                    onClick={() => deletePromoCode(promoCode)}
                                    icon="trash"
                                    float="right"
                                    style={{ color: '#7A7A7A', backgroundColor: 'white' }}
                                />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            {promoCodes.total_pages > 1 && (
                <tfoot>
                    <tr>
                        <td colSpan={8}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Pagination
                                    currentPage={promoCodes.current_page}
                                    onPageChange={handlePaginationChange}
                                    totalPages={promoCodes.total_pages}
                                />
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )}
        </AGTAbleWrapper>
    );
}

export default PromoCodeTable;
